import {makeGorillaApolloClient} from '@wandb/weave/apollo';
import {lazy} from 'react';

import {
  type RunsDataQueryConfig,
  toRunsDataQueryConfig,
} from '../../containers/RunsDataLoader';
import {apolloEndpoint} from '../../util/apollo';
import {createPreloadableLazyComponent} from '../../util/lazyImport';
import {
  WANDB_BBOX_CLASS_LABEL_KEY,
  WANDB_MASK_CLASS_LABEL_KEY,
} from '../../util/mediaConstants';
import type {PanelSpec} from '../../util/panelTypes';
import type {Query} from '../../util/queryTypes';
import {
  CES_EVAL_TABLE_HISTORY_TYPE,
  EVAL_TABLE_MAX_RUNS,
  EVALUATION_TABLES_PANEL_TYPE,
  type EvaluationTablesConfig,
  resolveEvalTableType,
} from './common';

// Singleton apollo client for the evaluation tables panel so we don't create
// a new client for each logged EvaluationTable.
let gorillaApolloClient: ReturnType<typeof makeGorillaApolloClient> | null =
  null;
export function getGorillaApolloClient(): ReturnType<
  typeof makeGorillaApolloClient
> {
  if (gorillaApolloClient == null) {
    gorillaApolloClient = makeGorillaApolloClient(apolloEndpoint);
  }
  return gorillaApolloClient;
}

/**
 * Active runsData required: the inner component reads `runsData.filtered` to
 * resolve workspace runs into eval call ids and to drive the run-slot strip.
 */
const transformQuery = (
  query: Query,
  config: EvaluationTablesConfig
): RunsDataQueryConfig => {
  // Eval Tables compare individual runs; grouped rows carry synthetic names
  // that can't match eval traces. Pass `disableGrouping` rather than stripping
  // grouping off the result — toRunsDataQueryConfig drops pinned runs from the
  // filter when it sees a grouped run set.
  const result = toRunsDataQueryConfig(
    query,
    {disableGrouping: true},
    {
      panelType: EVALUATION_TABLES_PANEL_TYPE,
      page: {size: EVAL_TABLE_MAX_RUNS},
    }
  );
  if (resolveEvalTableType(config) === CES_EVAL_TABLE_HISTORY_TYPE) {
    // CES image controls need the complete SDK class catalog for each column;
    // row media only includes classes present in that individual image.
    result.wandbKeys = [WANDB_MASK_CLASS_LABEL_KEY, WANDB_BBOX_CLASS_LABEL_KEY];
  }
  return result;
};

const configSpec = {};

export const Spec: PanelSpec<
  typeof EVALUATION_TABLES_PANEL_TYPE,
  EvaluationTablesConfig
> = {
  type: EVALUATION_TABLES_PANEL_TYPE,
  Component: createPreloadableLazyComponent(() => import('./Component'))
    .Component,
  transformQuery,
  configSpec,
  noEditMode: false,
  icon: 'table',
};
